import { CheckDetails } from '@ancon/wildcat-types'
import getFormattedCurrency from '@ancon/wildcat-utils/currency/getFormattedCurrency'
import getFormattedQuantity from '@ancon/wildcat-utils/quantity/getFormattedQuantity'

import { PayAndGoCartSummary } from '../types'

import getPayAndGoCheckDiscount from './getPayAndGoCheckDiscount'

function getCalculatedPayAndGoCartSummary<
  PropName extends keyof Item,
  Item extends { ids: string[] } & Record<PropName, number>,
>(
  originalCheck: CheckDetails,
  items: Item[],
  quantityProp: PropName,
): PayAndGoCartSummary | null {
  const { currency } = originalCheck.totalAmount

  let totalAmount = 0
  let taxAmount = 0
  let subtotalInclTaxAmount = 0
  let subtotalExclTaxAmount = 0
  let totalItemsCount = 0

  for (const item of items) {
    const checkItem = originalCheck.items.find(i => item.ids.includes(i.id))

    if (checkItem) {
      const selectedQuantity = item[quantityProp]
      const originalQuantity = checkItem.quantity

      const multiplier = selectedQuantity / originalQuantity

      totalAmount += checkItem.totalPriceInclTax.amount * multiplier

      taxAmount +=
        Math.max(
          0,
          checkItem.totalPriceInclTax.amount -
            checkItem.totalPriceExclTax.amount,
        ) * multiplier

      subtotalInclTaxAmount += checkItem.totalPriceInclTax.amount * multiplier

      subtotalExclTaxAmount += checkItem.totalPriceExclTax.amount * multiplier

      if (checkItem.totalPriceInclTax.amount > 0) {
        totalItemsCount += 1
      }
    }
  }

  const totalAmountText = getFormattedCurrency(
    Math.min(totalAmount, originalCheck.remainingAmountToPay.amount),
    currency,
    {
      trimTrailingZeros: false,
      showAsManyDecimalsAsNeeded: true,
    },
  )
  const taxAmountText = getFormattedCurrency(taxAmount, currency, {
    trimTrailingZeros: false,
    showAsManyDecimalsAsNeeded: true,
    maxAsManyDecimalsAsNeeded: 2,
  })
  const subtotalInclTaxAmountText = getFormattedCurrency(
    subtotalInclTaxAmount,
    currency,
    { trimTrailingZeros: false, showAsManyDecimalsAsNeeded: true },
  )
  const subtotalExclTaxAmountText = getFormattedCurrency(
    subtotalExclTaxAmount,
    currency,
    { trimTrailingZeros: false, showAsManyDecimalsAsNeeded: true },
  )

  const discount = getPayAndGoCheckDiscount(originalCheck)

  return {
    discount,
    subtotalExclTaxAmount: subtotalExclTaxAmountText,
    subtotalInclTaxAmount: subtotalInclTaxAmountText,
    taxAmount: taxAmountText,
    tipAmount: null,
    tipPercentage: null,
    totalAmount: totalAmountText,
    totalItemsCount: getFormattedQuantity(totalItemsCount),
    certainAmount: null,
  }
}

export default getCalculatedPayAndGoCartSummary
